import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { shopAPI } from '../../services/api';
import { useTonTransaction } from '../../hooks/useTonTransaction';
import { getTonNetworkChain } from '../../services/tonConnect/config';
import { getApiErrorMessage, isUserCancelledError, isTonConnectError } from '../utils/apiError';
import {
  startTonPayment,
  setTonPaymentFailed,
  setTonPaymentSuccess,
} from '../../store/slices/tonPaymentSlice';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatNumber } from '../../i18n/format';

const paymentUnavailable = () =>
  i18n.t('payment.unavailable', 'Payment service is temporarily unavailable. Please try again later.');

/**
 * Общий компонент блока кнопок покупки для модалок.
 * Визуально и функционально идентичен во всех модалках (товары, паки, бусты, DatumPass):
 *   • GRAM  — фиолетовая кнопка, сумма + иконка /gram.png, TON Connect → verify на бэке
 *   • Stars — жёлтая кнопка, сумма + ★, Telegram invoice → successful_payment на бэке
 * Начисление товара делает ТОЛЬКО бэкенд после подтверждения платежа.
 */
const GRAM_ICON = '/gram.png';

// On-chain confirmation polling after the wallet returned: verify every 3s for up to ~90s.
// If the transfer is still not indexed, the backend reconciler settles it later.
const VERIFY_POLL_MS = 3000;
const VERIFY_POLL_MAX = 30;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** «Buy for: 1.5 [gram]» — label with the currency icon instead of the word */
export function GramPrice({ amount, prefix }) {
  const { t } = useTranslation();
  return (
    <>
      {prefix ?? t('payment.buy_for', 'Buy for:')} {amount}
      <img className="app-btn__icon" src={GRAM_ICON} alt="GRAM" />
    </>
  );
}

/** «Buy for: 299 ★» */
export function StarsPrice({ amount, prefix }) {
  const { t } = useTranslation();
  return <>{prefix ?? t('payment.buy_for', 'Buy for:')} {formatNumber(amount)} ★</>;
}

export default function PurchaseActions({
  allowTON,
  allowStars,
  purchaseType = 'SHOP',
  itemId,
  usdPrice,
  priceTON,
  priceStars,
  description,
  tonLabel,
  starsLabel,
  disabled = false,
  onClose,
  /** Called once the payment is confirmed (TON verified on backend / Telegram invoice paid).
      Use it to refetch what the purchase granted (balances, DatumPass status, inventory). */
  onPaid,
  quantity = 1,
  // item info for TonPaymentStatusModal display
  itemLabel,
  itemImg,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [starsError, setStarsError] = useState(null);
  const {
    ensureWalletConnection,
    executeTransaction,
  } = useTonTransaction();

  const computedTonLabel = useMemo(() => {
    if (tonLabel) return tonLabel;
    // GRAM price comes from the backend (usdt / app_settings.GRAM_USD_RATE, DatumPass — price_ton)
    const unitTON = priceTON ? parseFloat(priceTON) : null;
    if (unitTON === null || Number.isNaN(unitTON)) return <GramPrice amount="" prefix={t('payment.buy_with', 'Buy with')} />;
    const total = (unitTON * quantity).toFixed(2);
    return <GramPrice amount={total} />;
  }, [priceTON, tonLabel, quantity, t]);

  const computedStarsLabel = useMemo(() => {
    if (starsLabel) return starsLabel;
    // Stars price comes from the backend (usdt / app_settings.STARS_USD_RATE or the item's own price_stars)
    const unitStars = priceStars ? Number(priceStars) : null;
    if (unitStars === null || Number.isNaN(unitStars)) return t('payment.buy_for_stars', 'Buy for ★');
    const total = unitStars * quantity;
    return <StarsPrice amount={total} />;
  }, [priceStars, starsLabel, quantity, t]);

  const handleStarsPurchase = async () => {
    setStarsError(null);
    try {
      setLoading(true);
      if (!itemId) throw new Error('ItemId is required');

      const result = await shopAPI.purchaseWithStars({
        purchaseType,
        itemId,
        quantity,
      });

      if (!result.success || !result.invoiceLink) {
        throw new Error(result.error || paymentUnavailable());
      }

      if (window.Telegram && window.Telegram.WebApp) {
        window.Telegram.WebApp.openInvoice(result.invoiceLink, (status) => {
          if (status === 'paid') {
            // Goods are granted by the backend on Telegram's successful_payment update
            onPaid?.({ method: 'STARS' });
            onClose?.();
          } else if (status === 'failed') {
            setStarsError(t('payment.failed_retry', 'Payment failed. Please try again.'));
          }
        });
      } else {
        setStarsError(t('payment.stars_only_telegram', 'Stars payments are only available inside Telegram.'));
      }
    } catch (err) {
      console.error('Stars purchase error:', err);
      setStarsError(getApiErrorMessage(err, paymentUnavailable()));
    } finally {
      setLoading(false);
    }
  };

  const handleTonPurchase = async () => {
    let paymentData = null;
    try {
      setLoading(true);
      if (!itemId) throw new Error('ItemId is required');

      paymentData = await shopAPI.initTonConnectPayment({
        purchaseType,
        itemId,
        description,
        quantity,
      });

      if (!paymentData.purchaseId || !paymentData.walletAddress || !paymentData.amountNanotons) {
        throw new Error(paymentUnavailable());
      }

      // Bare amount, no currency suffix — TonPaymentStatusModal appends it
      // (previously both sides added " TON" and the popup showed "0.5 TON TON").
      // The invoice amount from the backend (already × quantity) — exactly what the wallet will send.
      const tonAmount = paymentData.amount
        ? String(parseFloat(paymentData.amount))
        : priceTON ? String(priceTON) : null;

      // Open status modal and close purchase modal BEFORE TonConnect UI opens
      dispatch(startTonPayment({
        purchaseId: paymentData.purchaseId,
        item: { id: itemId, label: itemLabel, img: itemImg },
        tonAmount,
        usdAmount: usdPrice ? String(usdPrice) : null,
        walletAddress: paymentData.walletAddress,
      }));

      onClose?.();
      setLoading(false);

      // Now TonConnect UI can render without z-index conflict
      await ensureWalletConnection();

      // `payload` = text comment with the purchase id (built by the backend). The backend finds the
      // transfer on-chain by this comment + amount and only then grants the goods — the hash we
      // send to verify is informational, never trusted.
      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600,
        network: getTonNetworkChain(), // '-3' testnet / '-239' mainnet — wallet must be on the same network
        messages: [
          {
            address: paymentData.walletAddress,
            amount: paymentData.amountNanotons,
            ...(paymentData.payload ? { payload: paymentData.payload } : {}),
          },
        ],
      };

      const { result } = await executeTransaction(transaction);
      if (!result) {
        // User cancelled — silent, just close status modal
        dispatch(setTonPaymentFailed({ purchaseId: paymentData.purchaseId, error: null }));
        return;
      }

      const txHash = result.boc || result.txid || result.hash || JSON.stringify(result);
      let verify = await shopAPI.verifyTonTransaction(paymentData.purchaseId, txHash, undefined);
      for (let attempt = 0; verify.pending && attempt < VERIFY_POLL_MAX; attempt++) {
        await sleep(VERIFY_POLL_MS);
        verify = await shopAPI.verifyTonTransaction(paymentData.purchaseId, txHash, undefined);
      }

      if (verify.success) {
        dispatch(setTonPaymentSuccess({ purchaseId: paymentData.purchaseId }));
        onPaid?.({ method: 'TON', purchaseId: paymentData.purchaseId, confirmed: true });
      } else if (verify.pending) {
        // Sent but not indexed yet — the reconciler grants it as soon as the network confirms
        dispatch(setTonPaymentSuccess({ purchaseId: paymentData.purchaseId }));
        onPaid?.({ method: 'TON', purchaseId: paymentData.purchaseId, confirmed: false });
      } else {
        dispatch(setTonPaymentFailed({
          purchaseId: paymentData.purchaseId,
          error: verify.message || verify.error || t('payment.verification_failed', 'Verification failed'),
        }));
      }
    } catch (err) {
      console.error('TON Connect purchase error:', err);

      if (isUserCancelledError(err)) {
        // User closed / rejected in the wallet — nothing to explain, just close the status modal
        dispatch(setTonPaymentFailed({ purchaseId: paymentData?.purchaseId ?? null, error: null }));
      } else if (isTonConnectError(err)) {
        dispatch(setTonPaymentFailed({
          purchaseId: paymentData?.purchaseId ?? null,
          error: t('payment.wallet_failed', 'The wallet could not send the transaction. Check that it is connected and on the right network, then try again.'),
        }));
      } else {
        dispatch(setTonPaymentFailed({
          purchaseId: paymentData?.purchaseId ?? null,
          error: getApiErrorMessage(err, paymentUnavailable()),
        }));
      }

      setLoading(false);
    }
  };

  return (
    <div className="purchase-modal-buttons">
      {starsError && (
        <p style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 8, textAlign: 'center' }}>
          {starsError}
        </p>
      )}

      {allowTON && (
        <button
          className="app-btn app-btn--purple"
          onClick={handleTonPurchase}
          disabled={loading || disabled}
        >
          {loading ? t('common.processing', 'Processing…') : computedTonLabel}
        </button>
      )}

      {allowStars && (
        <button
          className="app-btn app-btn--yellow"
          onClick={handleStarsPurchase}
          disabled={loading || disabled}
        >
          {loading ? t('common.processing', 'Processing…') : computedStarsLabel}
        </button>
      )}
    </div>
  );
}
